import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import apiClient from '../api/client';

interface HealthResponse {
  status: string;
  timestamp: string;
}

interface ReadinessResponse {
  status: string;
  timestamp: string;
  checks: Record<string, string>;
}

async function fetchHealth(): Promise<HealthResponse> {
  const response = await apiClient.get<HealthResponse>('/health');
  return response.data;
}

async function fetchReadiness(): Promise<ReadinessResponse> {
  const response = await apiClient.get<ReadinessResponse>('/health/ready');
  return response.data;
}

export function HealthCheck() {
  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: fetchHealth,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const readinessQuery = useQuery({
    queryKey: ['health', 'ready'],
    queryFn: fetchReadiness,
    refetchInterval: 30000,
  });

  const handleRefresh = () => {
    healthQuery.refetch();
    readinessQuery.refetch();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Typography variant="h4">System Health</Typography>
        <IconButton onClick={handleRefresh} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Basic Health */}
        <Card sx={{ minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Health
            </Typography>

            {healthQuery.isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography>Checking...</Typography>
              </Box>
            )}

            {healthQuery.isError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                <ErrorIcon />
                <Typography>Backend unavailable</Typography>
              </Box>
            )}

            {healthQuery.isSuccess && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CheckCircleIcon color="success" />
                  <Chip
                    label={healthQuery.data.status.toUpperCase()}
                    color="success"
                    size="small"
                  />
                </Box>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Last checked: {new Date(healthQuery.data.timestamp).toLocaleString()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Readiness */}
        <Card sx={{ minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Readiness
            </Typography>

            {readinessQuery.isLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography>Checking...</Typography>
              </Box>
            )}

            {readinessQuery.isError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                <ErrorIcon />
                <Typography>Backend unavailable</Typography>
              </Box>
            )}

            {readinessQuery.isSuccess && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {readinessQuery.data.status === 'ready' ? (
                    <CheckCircleIcon color="success" />
                  ) : (
                    <ErrorIcon color="error" />
                  )}
                  <Chip
                    label={readinessQuery.data.status.toUpperCase()}
                    color={readinessQuery.data.status === 'ready' ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Dependency Checks:
                </Typography>
                {Object.entries(readinessQuery.data.checks).map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}
                  >
                    <Typography variant="body2">{key}</Typography>
                    <Chip
                      label={value}
                      color={value === 'ok' ? 'success' : 'error'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                ))}

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 2
                  }}>
                  Last checked: {new Date(readinessQuery.data.timestamp).toLocaleString()}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
